import React from 'react';
import { FaFolder, FaFile } from 'react-icons/fa';
import { DocumentItem } from '../../../types/appModels';
import css from './DocumentItem.module.css';

interface DocumentItemProps {
  item: DocumentItem;
  formattedName: string;
  isSelected: boolean;
  onItemClick: () => void;
  onFileDownload: (path: string) => void;
}

const DocumentItemComponent: React.FC<DocumentItemProps> = ({
  item,
  formattedName,
  isSelected,
  onItemClick,
  onFileDownload
}) => {
  // Decode the formatted name for display
  const displayName = decodeURIComponent(formattedName);
  
  const handleClick = () => {
    onItemClick();
  };

  const handleDoubleClick = () => {
    if (item.type === 'File') {
      onFileDownload(item.path);
    }
  };

  const isFolder = item.type === 'Folder';
  const isSelectable = !isFolder; // Only files are selectable
  
  // Determine file type and select color
  const getFileIconClass = (): string => {
    if (isFolder) return css.folderIcon;
    
    const fileName = item.name.toLowerCase();
    const extension = fileName.split('.').pop() || '';

    // Spreadsheet files (green)
    if (['csv', 'xlsx', 'xls', 'xlsm', 'xlsb', 'xltx', 'xltm', 'xlt'].includes(extension)) {
      return `${css.fileIcon} ${css.spreadsheetIcon}`;
    }
    
    // PDF files (red)
    if (extension === 'pdf') {
      return `${css.fileIcon} ${css.pdfIcon}`;
    }
    
    // Document files (blue)
    if (['doc', 'docx', 'docm', 'dot', 'dotx', 'dotm', 'rtf'].includes(extension)) {
      return `${css.fileIcon} ${css.documentIcon}`;
    }
    
    // Default file icon
    return css.fileIcon;
  };
  
  return (
    <div
      className={`${css.item} ${isSelected ? css.selected : ''} ${isSelectable ? css.selectable : ''}`}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      <div className={css.itemContent}>
        <div className={css.iconContainer}>
          {isFolder ? 
            <FaFolder className={css.folderIcon} /> : 
            <FaFile className={getFileIconClass()} />
          }
        </div>
        <span className={css.itemName}>
          {displayName}
        </span>
      </div>
    </div>
  );
};

export default DocumentItemComponent;